import { PageHeaderWrapper } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useGetShiftIdWithOrientationRequired } from "@src/appV2/redesign/Shift/OrientationBlock/useGetShiftIdWithOrientationRequired";
import { SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";
import { generatePath, useHistory, useParams } from "react-router-dom";

import { useGetShiftBlock } from "./api/useGetShiftBlock";
import { BookShiftBlockButton } from "./BookShiftBlockButton";
import { ShiftBlockCardSkeleton } from "./ShiftBlockCardSkeleton";
import { ShiftBlockDetails } from "./ShiftBlockDetails";
import {
  getHourlyPayForShiftBlock,
  isShiftBlockBookedByAgentId,
  transformIntoShiftBlockWithDetails,
} from "./utils";

export function ShiftBlockDetailsPage() {
  const { shiftBlockId } = useParams<{
    shiftBlockId: string;
  }>();
  const worker = useDefinedWorker();
  const history = useHistory();

  const {
    data: shiftBlock,
    isLoading: isShiftBlockLoading,
    isSuccess: isShiftBlockSuccess,
    refetch: refetchShiftBlock,
  } = useGetShiftBlock(
    { shiftBlockId, agentId: worker.userId },
    { enabled: isDefined(shiftBlockId) }
  );

  const shiftIdWithOrientationRequired = useGetShiftIdWithOrientationRequired();

  const shiftBlockWithMappedDetails = isShiftBlockSuccess
    ? transformIntoShiftBlockWithDetails(shiftBlock.data, shiftBlock.included)
    : undefined;

  const isShiftBlockBooked =
    isDefined(shiftBlockWithMappedDetails) &&
    isShiftBlockBookedByAgentId(shiftBlockWithMappedDetails, worker.userId);

  const isOrientationBlock = isDefined(shiftBlock?.data?.attributes?.orientationBlockId);

  const isOrientationBlockBooked = isOrientationBlock && isShiftBlockBooked;

  const defaultBackTo =
    isOrientationBlockBooked && isDefined(shiftIdWithOrientationRequired)
      ? generatePath(SHIFT_DISCOVERY_SHIFT_MODAL_FULL_PATH, {
          shiftId: shiftIdWithOrientationRequired,
        })
      : DeprecatedGlobalAppV1Paths.OPEN_SHIFTS;

  const isShiftBlockNegotiable =
    isDefined(shiftBlock) && Boolean(shiftBlock.data.attributes.isNegotiable);

  const handleOrientationBlockGoBack = useCallback(() => {
    history.push(defaultBackTo);
  }, [defaultBackTo, history]);

  return (
    <ThemeProvider theme={getTheme()}>
      <CssBaseline />
      <PageWrapper>
        <PageHeaderWrapper>
          <PageHeaderTopRow
            title={isShiftBlockBooked ? "My Booked Block Details" : "Open Block Details"}
            fallbackGoBackPath={defaultBackTo}
            {...(isOrientationBlockBooked && { onClickBack: handleOrientationBlockGoBack })}
          />
        </PageHeaderWrapper>

        <PageContent>
          {isShiftBlockLoading && <ShiftBlockCardSkeleton />}
          {isShiftBlockSuccess && <ShiftBlockDetails worker={worker} shiftBlock={shiftBlock} />}
        </PageContent>

        {isShiftBlockSuccess && isDefined(shiftBlockWithMappedDetails) && !isShiftBlockBooked && (
          <PageFooterWrapper>
            <BookShiftBlockButton
              isBlockNegotiable={isShiftBlockNegotiable}
              facilityId={shiftBlock.data.attributes.facilityId}
              agentId={worker.userId}
              shiftBlockId={shiftBlock.data.id}
              shifts={shiftBlockWithMappedDetails.shifts}
              blockAgentRequirement={shiftBlock.data.attributes.agentReq}
              instantBookPreferredReasons={
                shiftBlock.data.attributes.instantBook?.preferredReasons ?? []
              }
              hourlyPayForShiftBlock={getHourlyPayForShiftBlock(shiftBlockWithMappedDetails)}
              workplaceName={shiftBlockWithMappedDetails.facility.name}
              workerName={worker.name}
              orientationBlockId={
                isDefined(shiftBlockWithMappedDetails.orientationBlockId)
                  ? shiftBlockWithMappedDetails.orientationBlockId
                  : undefined
              }
              onBookingShiftBlock={refetchShiftBlock}
            />
          </PageFooterWrapper>
        )}
      </PageWrapper>
    </ThemeProvider>
  );
}
